/**
 * Wheelify client-side translator for nl./de. storefronts.
 *
 * Why this exists: the wheel's slice labels are rendered as SVG <text>/<tspan>
 * elements, and Google Translate / Chrome's built-in page translation never
 * translates text inside SVG (it only walks normal HTML text nodes). Form
 * placeholders ("Enter your name") are attribute values, which browser
 * translate also skips. This script rewrites both directly, client-side,
 * for the Dutch and German storefronts.
 *
 * WHERE TO PLACE THIS:
 *   theme.liquid, just before </body> — so it loads on every page the
 *   wheel can appear on, not just one template.
 *
 * IMPORTANT LIMITATION - read before relying on this:
 * Every piece of wheel copy (popup heading/subtitle, win heading, CTA
 * button label, consent text, slice labels, etc.) is a free-text field a
 * merchant can type per campaign - there is no fixed, finite set of English
 * strings to translate. This file's DICTIONARY only covers:
 *   (a) the app's own hardcoded fallback defaults (used when a merchant
 *       leaves a field blank), and
 *   (b) specific custom campaign copy someone has manually added after
 *       noticing it wasn't translated.
 * Any campaign with its own custom heading/win-text/button copy that isn't
 * in DICTIONARY yet will show untranslated until someone adds it here.
 * This is a stopgap, not a complete solution - the real fix is per-language
 * campaign text fields in the admin, served per shop locale by the API.
 *
 * MAINTENANCE: when a new/edited campaign shows untranslated text on nl./de.,
 * add the exact source string (and its translation) to DICTIONARY below.
 * The PATTERNS list below already covers the common "N% OFF" / "Free X"
 * shapes so most discount slice labels need no new dictionary entry.
 */
(function () {
  "use strict";

  // ---- 1. Which locale is this storefront? ---------------------------------
  // Gated strictly by domain - nl. is Dutch (Netherlands), de. is German.
  // Any other domain (including the main store) returns null below and the
  // whole script is a no-op there.
  function detectLocale() {
    var host = window.location.hostname.toLowerCase();
    if (host.indexOf("nl.") === 0) return "nl";
    if (host.indexOf("de.") === 0) return "de";
    return null; // not nl./de. - leave everything as-is
  }

  var LOCALE = detectLocale();
  if (!LOCALE) return;

  // ---- 2. Exact-phrase dictionary ------------------------------------------
  // Add new campaign copy here as it's created. Matching is done against the
  // trimmed, case-sensitive text exactly as Wheelify renders it.
  var DICTIONARY = {
    nl: {
      "Almost there": "Bijna gelukt",
      "So close": "Zo dichtbij",
      "Better luck next time": "Volgende keer beter",
      "No luck today": "Geen geluk vandaag",
      "Sorry": "Helaas",
      "Free shipping": "Gratis verzending",
      "Spin to win": "Draai en win",
      "Try Your Luck!": "Probeer je geluk!",
      "SPIN IF YOU DARE": "DRAAI ALS JE DURFT",
      "Enter your name": "Vul je naam in",
      "Enter your email": "Vul je e-mailadres in",
      "Enter your full name and email to spin the wheel for a chance to win":
        "Vul je volledige naam en e-mailadres in om aan het rad te draaien voor een kans om te winnen",
      "I agree to my personal data being stored and used to receive the newsletter":
        "Ik ga akkoord dat mijn persoonlijke gegevens worden opgeslagen en gebruikt om de nieuwsbrief te ontvangen",
      "I agree to receive exclusive offers and updates from Huge Supplements.":
        "Ik ga akkoord met het ontvangen van exclusieve aanbiedingen en updates van Huge Supplements.",
      "Click 'Apply Discount', or copy the code.":
        "Klik op 'Korting toepassen' of kopieer de code.",
      "Apply Discount": "Korting toepassen",
      "YOU GOT YOURSELF A TREAT!": "JE HEBT EEN TRAKTATIE GESCOORD!",
      "Click 'Claim your Treat', or copy the code.":
        "Klik op 'Claim je traktatie' of kopieer de code.",
      "Claim your Treat": "Claim je traktatie",

      // Hardcoded fallback defaults (utils/index.js in the theme-extension repo) -
      // used by any campaign that didn't override the field. Anything the
      // merchant DID type a custom value for (like the two above) still needs
      // its own entry added here by hand - see file header.
      "Enter your details to spin the wheel": "Vul je gegevens in om aan het rad te draaien",
      "Get a chance to win exciting prizes!": "Maak kans op geweldige prijzen!",
      "Enter your phone number": "Vul je telefoonnummer in",
      "Submit & Spin": "Verzenden & Draaien",
      "Won a Prize": "Een prijs gewonnen",
      "You Won!": "Je hebt gewonnen!",
      "Click to Apply your discount automatically at checkout!":
        "Klik om je korting automatisch toe te passen bij het afrekenen!",
      "Discount Applied!": "Korting toegepast!",
      "Copy Code": "Code kopiëren",
      "Copied": "Gekopieerd",
      "Don't forget to copy your coupon code!": "Vergeet niet je kortingscode te kopiëren!",
      "Don't forget to copy your coupon code before closing.":
        "Vergeet niet je kortingscode te kopiëren voordat je dit sluit.",
      "You have reached the spin limit. Please try again later.":
        "Je hebt het maximale aantal draaibeurten bereikt. Probeer het later opnieuw.",
      "Hurry! Limited time offer.": "Snel! Tijdelijke aanbieding.",

      // Urgency timer bar (custom per-campaign text, split across text nodes
      // by <strong> tags wrapping the coupon code and countdown - each
      // fragment is translated independently since translateTextNodesIn
      // walks individual text nodes).
      "🎃 YOUR TREAT IS WAITING! Claim your discount code":
        "🎃 JE TRAKTATIE WACHT! Gebruik je kortingscode",
      "within": "binnen",
      "before it disappears!": "voordat het verdwijnt!",
    },
    de: {
      "Almost there": "Fast geschafft",
      "So close": "So nah dran",
      "Better luck next time": "Nächstes Mal mehr Glück",
      "No luck today": "Heute kein Glück",
      "Sorry": "Leider nicht",
      "Free shipping": "Kostenloser Versand",
      "Spin to win": "Drehen und gewinnen",
      "Try Your Luck!": "Versuch dein Glück!",
      "SPIN IF YOU DARE": "DREH, WENN DU DICH TRAUST",
      "Enter your name": "Namen eingeben",
      "Enter your email": "E-Mail eingeben",
      "Enter your full name and email to spin the wheel for a chance to win":
        "Gib deinen vollständigen Namen und deine E-Mail-Adresse ein, um am Rad zu drehen und zu gewinnen",
      "I agree to my personal data being stored and used to receive the newsletter":
        "Ich bin damit einverstanden, dass meine personenbezogenen Daten gespeichert und für den Newsletter verwendet werden",
      "I agree to receive exclusive offers and updates from Huge Supplements.":
        "Ich bin damit einverstanden, exklusive Angebote und Updates von Huge Supplements zu erhalten.",
      "Click 'Apply Discount', or copy the code.":
        "Klicke auf 'Rabatt anwenden' oder kopiere den Code.",
      "Apply Discount": "Rabatt anwenden",
      "YOU GOT YOURSELF A TREAT!": "DU HAST DIR EINE LECKEREI VERDIENT!",
      "Click 'Claim your Treat', or copy the code.":
        "Klicke auf 'Hol dir deine Belohnung' oder kopiere den Code.",
      "Claim your Treat": "Hol dir deine Belohnung",

      // Hardcoded fallback defaults (utils/index.js in the theme-extension repo) -
      // used by any campaign that didn't override the field. Anything the
      // merchant DID type a custom value for (like the two above) still needs
      // its own entry added here by hand - see file header.
      "Enter your details to spin the wheel": "Gib deine Daten ein, um am Rad zu drehen",
      "Get a chance to win exciting prizes!": "Erhalte die Chance auf tolle Preise!",
      "Enter your phone number": "Telefonnummer eingeben",
      "Submit & Spin": "Absenden & Drehen",
      "Won a Prize": "Einen Preis gewonnen",
      "You Won!": "Du hast gewonnen!",
      "Click to Apply your discount automatically at checkout!":
        "Klicke, um deinen Rabatt automatisch an der Kasse anzuwenden!",
      "Discount Applied!": "Rabatt angewendet!",
      "Copy Code": "Code kopieren",
      "Copied": "Kopiert",
      "Don't forget to copy your coupon code!": "Vergiss nicht, deinen Gutscheincode zu kopieren!",
      "Don't forget to copy your coupon code before closing.":
        "Vergiss nicht, deinen Gutscheincode zu kopieren, bevor du dies schließt.",
      "You have reached the spin limit. Please try again later.":
        "Du hast das Drehlimit erreicht. Bitte versuche es später erneut.",
      "Hurry! Limited time offer.": "Schnell! Zeitlich begrenztes Angebot.",

      // Urgency timer bar (custom per-campaign text, split across text nodes
      // by <strong> tags wrapping the coupon code and countdown - each
      // fragment is translated independently since translateTextNodesIn
      // walks individual text nodes).
      "🎃 YOUR TREAT IS WAITING! Claim your discount code":
        "🎃 DEINE BELOHNUNG WARTET! Verwende deinen Rabattcode",
      "within": "innerhalb von",
      "before it disappears!": "bevor er verschwindet!",
    },
  };

  // ---- 3. Pattern-based rules (for text with numbers/custom words in it) ---
  // Each rule: a regex to test against the trimmed source text, and a
  // replace() that returns the translated string. Order matters - first
  // match wins. `translate()` is called recursively so a captured group
  // (e.g. a prize name that is itself "20% OFF") gets translated too.
  var PATTERNS = {
    nl: [
      { re: /^(\d+)%\s*OFF$/i, replace: function (m) { return m[1] + "% KORTING"; } },
      { re: /^Free\s+(.+)$/i, replace: function (m) { return "Gratis " + m[1]; } },
      { re: /^You won "(.+)"$/, replace: function (m) { return 'Je hebt "' + translate(m[1]) + '" gewonnen'; } },
      {
        re: /^Spin the wheel for a chance to win exclusive discounts up to (\d+)% OFF or FREE products$/i,
        replace: function (m) {
          return "Draai aan het rad voor een kans op exclusieve kortingen tot " + m[1] + "% KORTING of GRATIS producten";
        },
      },
      { re: /^(.+) - Spin & Save BIG!$/, replace: function (m) { return m[1] + " - Draai & Bespaar GROOT!"; } },
      { re: /^TRICK OR TREAT\?(.*)$/i, replace: function (m) { return "TRUC OF TRAKTATIE?" + m[1]; } },
    ],
    de: [
      { re: /^(\d+)%\s*OFF$/i, replace: function (m) { return m[1] + "% RABATT"; } },
      { re: /^Free\s+(.+)$/i, replace: function (m) { return "Gratis " + m[1]; } },
      { re: /^You won "(.+)"$/, replace: function (m) { return 'Du hast "' + translate(m[1]) + '" gewonnen'; } },
      {
        re: /^Spin the wheel for a chance to win exclusive discounts up to (\d+)% OFF or FREE products$/i,
        replace: function (m) {
          return "Dreh am Rad für die Chance auf exklusive Rabatte bis zu " + m[1] + "% RABATT oder GRATIS Produkte";
        },
      },
      { re: /^(.+) - Spin & Save BIG!$/, replace: function (m) { return m[1] + " - Drehen & Groß sparen!"; } },
      { re: /^TRICK OR TREAT\?(.*)$/i, replace: function (m) { return "SÜSSES ODER SAURES?" + m[1]; } },
    ],
  };

  // ---- 4. Core translate() --------------------------------------------------
  function translate(text) {
    var trimmed = (text || "").trim();
    if (!trimmed) return text;

    var dict = DICTIONARY[LOCALE] || {};
    if (Object.prototype.hasOwnProperty.call(dict, trimmed)) {
      return text.replace(trimmed, dict[trimmed]);
    }

    // Prefix match: merchants often type a decorative emoji straight into a
    // heading/win-text field (e.g. admin "Win text" = "YOU GOT YOURSELF A
    // TREAT! 🎃" as ONE field value) - that never equals a plain dictionary
    // key exactly. Find the longest dictionary key the text starts with,
    // translate just that part, and keep whatever trails it (the emoji)
    // untouched, rather than requiring a separate pattern per field.
    var bestKey = null;
    Object.keys(dict).forEach(function (key) {
      if (trimmed.indexOf(key) === 0 && (!bestKey || key.length > bestKey.length)) {
        bestKey = key;
      }
    });
    if (bestKey) {
      return text.replace(bestKey, dict[bestKey]);
    }

    var rules = PATTERNS[LOCALE] || [];
    for (var i = 0; i < rules.length; i++) {
      var match = trimmed.match(rules[i].re);
      if (match) return text.replace(trimmed, rules[i].replace(match));
    }

    return text; // no known translation - leave untouched rather than guess
  }

  // ---- 5. Applying translations to the live widget --------------------------
  var WIDGET_SELECTOR = "#wheelify-spin_a_sale_cc_store_front_module, #wheelify";
  var PLACEHOLDER_SELECTORS = [
    "#cc_spinner_full_name",
    "#cc_spinner_email",
    'input[name="phone"]',
    'input[type="email"]',
    'input[type="tel"]',
    'input[type="text"]',
  ];

  function translateTextNodesIn(root) {
    // Walk actual TEXT nodes, not leaf elements. Some paragraphs mix plain
    // text with inline elements - e.g. the win heading renders as
    // `<p>"YOU GOT YOURSELF A TREAT! 🎃" " " <span>20% OFF</span> "\""</p>`,
    // where the heading text is a text node sitting next to a <span> sibling.
    // Checking el.children.length === 0 skips that whole <p> (it has a child
    // element) and its direct text is never reached. Processing text nodes
    // directly handles this and the plain-text-only case identically.
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node;
    var textNodes = [];
    while ((node = walker.nextNode())) {
      if (node.nodeValue && node.nodeValue.trim()) textNodes.push(node);
    }
    textNodes.forEach(function (node) {
      var next = translate(node.nodeValue);
      if (next !== node.nodeValue) node.nodeValue = next;
    });
  }

  function translatePlaceholdersIn(root) {
    PLACEHOLDER_SELECTORS.forEach(function (selector) {
      root.querySelectorAll(selector).forEach(function (input) {
        var ph = input.getAttribute("placeholder");
        if (!ph) return;
        var next = translate(ph);
        if (next !== ph) input.setAttribute("placeholder", next);
      });
    });
  }

  function translateWidget(root) {
    translateTextNodesIn(root);
    translatePlaceholdersIn(root);
  }

  // ---- 6. Wait for the widget, then keep re-translating as it re-renders ---
  // The widget is injected asynchronously by the app, and its content swaps
  // between the entry form / spinning state / win screen, so a one-off pass
  // isn't enough - a MutationObserver re-runs translate() on every change.
  // Re-running is safe: once text is Dutch/German it no longer matches an
  // English dictionary/pattern entry, so it's left alone on the next pass.
  var widgetObserver = null;

  function attachWidgetObserver(root) {
    if (widgetObserver) widgetObserver.disconnect();
    widgetObserver = new MutationObserver(function () {
      widgetObserver.disconnect();
      translateWidget(root);
      widgetObserver.observe(root, { childList: true, subtree: true, characterData: true });
    });
    translateWidget(root);
    widgetObserver.observe(root, { childList: true, subtree: true, characterData: true });
  }

  function waitForWidget() {
    var existing = document.querySelector(WIDGET_SELECTOR);
    if (existing) {
      attachWidgetObserver(existing);
      return;
    }
    var bodyObserver = new MutationObserver(function () {
      var found = document.querySelector(WIDGET_SELECTOR);
      if (found) {
        bodyObserver.disconnect();
        attachWidgetObserver(found);
      }
    });
    bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", waitForWidget);
  } else {
    waitForWidget();
  }
})();
